import type { UserPermissions } from "@amsaas/shared";
import { trpc } from "../lib/trpc";

/**
 * The acting user's effective permission flags (doc 23-billing §12).
 *
 * Reads the SAME cached `auth.me` query `useActiveUser` already issues, so gating the
 * shell costs no extra request. This is presentation only — every procedure re-checks
 * the flag server-side, and hiding a menu entry is never the enforcement boundary.
 *
 * A Provider Admin implicitly holds every flag (the server treats admin as a bypass in
 * `requireInvoiceWrite`/`requireFinancialsRead`), so `can()` mirrors that here rather
 * than depending on the role row happening to have them all set.
 */
export interface PermissionState {
  isLoading: boolean;
  isProviderAdmin: boolean;
  permissions: UserPermissions | null;
  /** True when the user may exercise `flag` — admins always may. */
  can: (flag: keyof UserPermissions) => boolean;
}

export function usePermissions(): PermissionState {
  const me = trpc.auth.me.useQuery(undefined, { retry: false });
  const userType = me.data?.user.userType ?? null;
  const isProviderAdmin = userType === "provider_admin";
  const permissions = me.data?.permissions ?? null;

  const can = (flag: keyof UserPermissions): boolean => {
    if (flag === "isReadOnly") return permissions?.isReadOnly === true;
    if (isProviderAdmin) return true;
    return permissions?.[flag] === true;
  };

  return { isLoading: me.isLoading, isProviderAdmin, permissions, can };
}
